import React, { useState } from 'react';
import { Box } from '@mui/material';
import { unescape } from 'lodash-es';

import {
  EmailAttachments,
  EmailDetailsToolbar,
  EmailReply,
  EmailThread,
  Sender,
} from '@components';
import { Loader } from 'components/common/ui';
import { communicationServices } from '@services';
import { showError, showSuccess } from '@utils';
import { styles } from './emailViewDetail.style';
import TeamChip from '../TeamChip/TeamChip';
import AssigneeChip from '../AssigneeChip/AssigneeChip';
import StatusChip from '../StatusChip/StatusChip';
import EmailListItemPriority from '../EmailView/EmailListItemPriority';
import EmailIframeViewer from '../EmailThread/EmailIframeViewer';
import { CONNECT_MAIL_TO_ENTITY_ERROR } from '../constants';

export const REPLY_CONTAINER_ID = 'reply-container';
const EmailViewDetail = ({
  email,
  assignees = [],
  refetch,
  thread = {},
  isLoading,
  agent,
  isFetching,
  assigneesMap,
  hasConnectedEntity,
}) => {
  const [loading, setIsLoading] = useState(false);
  const [openReply, setIsOpenReply] = useState(false);
  const [isForward, setIsForward] = useState(false);

  const handleToggleReply = () => {
    setIsOpenReply(!openReply);
  };

  const handleCloseReply = () => {
    setIsOpenReply(false);
    setIsForward(false);
  };

  const handleScrollToReply = () => {
    if (!hasConnectedEntity) {
      showError(CONNECT_MAIL_TO_ENTITY_ERROR);
      return;
    }
    setIsOpenReply(true);
    setIsForward(false);

    setTimeout(() => {
      const footer = document.querySelector(`#${REPLY_CONTAINER_ID}`);
      footer?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleOpenForward = () => {
    if (!hasConnectedEntity) {
      showError(CONNECT_MAIL_TO_ENTITY_ERROR);
      return;
    }
    setIsOpenReply(false);
    setIsForward(true);

    setTimeout(() => {
      const footer = document.querySelector(`#${REPLY_CONTAINER_ID}`);
      footer?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = async ({
    body,
    receivers,
    ccReceivers,
    attachments,
    attachmentIds,
    templateId,
    templateLang,
  }) => {
    if (!receivers?.length) {
      showError('Please add receivers');
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        id: email.id,
        cc: ccReceivers,
        to: receivers,
        from: email.mailboxAddress,
        body,
        subject: isForward ? `Fwd: ${email.subject}` : email.subject,
        mailboxId: email.mailboxId,
      };

      const formData = new FormData();
      formData.append('id', payload.id);
      formData.append('from', payload.from);
      formData.append('body', payload.body);
      formData.append('subject', payload.subject);
      formData.append('mailboxId', payload.mailboxId);

      if (payload.cc && payload.cc.length > 0) {
        formData.append('cc', JSON.stringify(payload.cc));
      }

      if (payload.to && payload.to.length > 0) {
        formData.append('to', JSON.stringify(payload.to));
      }

      if (attachments && attachments.length > 0) {
        attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }
      // send existing attachment ids from the template email (if any)
      if (attachmentIds && attachmentIds.length > 0) {
        formData.append('attachmentIds', JSON.stringify(attachmentIds));
      }

      if (templateId) {
        formData.append('templateId', templateId);
      }
      if (templateLang) {
        formData.append('templateLang', templateLang);
      }

      await communicationServices.replyEmailV2(formData);

      showSuccess('Email sent successfully!');

      await refetch();

      handleToggleReply();
    } catch (error) {
      showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatchEmail = async (payload) => {
    try {
      let emailIds = [email?.id];

      if (thread?.length) {
        emailIds = [...emailIds, ...thread.map((item) => item.id)];
      }

      await communicationServices.editEmails({ emailIds, action: payload });
      await refetch();
    } catch (error) {
      showError(error);
    }
  };

  const isReplyToMailboxAddress =
    email.mailboxAddress === email.to?.[0]?.emailAddress;

  const user = isReplyToMailboxAddress
    ? {
        email: email.mailboxAddress,
        name: email.mailboxAddress,
      }
    : email?.from;

  const html = email?.body ? unescape(email?.body) : unescape(email?.snippet);
  // when email body contains style tag we show it in iframe viewer because of styling issues
  // some styles may break the layout of the page
  const shouldShowEmailToIframe = /<style\b[^>]*>[\s\S]*?<\/style>/i.test(html);

  return (
    <Box sx={styles.wrapper}>
      <EmailDetailsToolbar
        email={email}
        assignees={assignees}
        onPatchEmail={handlePatchEmail}
      />
      {isLoading && <Loader />}
      {email?.id && (
        <Box sx={styles.content}>
          <Box sx={styles.header}>
            <EmailListItemPriority priority={email.priority} />
            <Box sx={styles.title}>{email?.subject}</Box>

            <Box sx={styles.chipContainer}>
              <TeamChip team={email?.team} />
              <AssigneeChip assignee={assigneesMap[email?.assigneeEmail]} />
              <StatusChip status={email?.status} />
            </Box>
          </Box>
          <EmailThread data={thread} />

          <Sender
            user={email.from}
            to={email?.to}
            cc={email?.cc}
            bcc={email?.bcc}
            date={email?.date}
            onOpenReply={handleScrollToReply}
            onOpenForward={handleOpenForward}
          />
          <Box sx={styles.fullTextWrapper}>
            {shouldShowEmailToIframe ? (
              <EmailIframeViewer html={email.body || ''} />
            ) : (
              <Box
                variant="div"
                dangerouslySetInnerHTML={{
                  __html: html,
                }}
              />
            )}
          </Box>
          <Box sx={styles.attachments}>
            <EmailAttachments
              attachments={email?.attachments}
              emailId={email.id}
              loading={loading}
            />
          </Box>

          {isForward && (
            <EmailReply
              user={user}
              isOpen={isForward}
              loading={loading || isFetching}
              email={email}
              agent={agent}
              hasConnectedEntity={hasConnectedEntity}
              onToggleReply={handleToggleReply}
              onCloseReply={handleCloseReply}
              onSubmit={handleSubmit}
              isForward
            />
          )}
          <EmailReply
            user={user}
            isOpen={openReply}
            loading={loading || isFetching}
            email={email}
            agent={agent}
            hasConnectedEntity={hasConnectedEntity}
            onToggleReply={handleToggleReply}
            onCloseReply={handleCloseReply}
            onSubmit={handleSubmit}
            isForward={isForward}
          />
        </Box>
      )}
    </Box>
  );
};

export default EmailViewDetail;
